import React, { useState } from 'react';
import { C, DEPTS } from '../constants';
import { validateContactForm } from '../services/validation';
import { currentClaims, isTecnico, isSignedToken, profileLabel } from '../services/auth';
import { tokenParaBackend } from '../services/adminAuth';
import { hubPost } from '../services/hub';

const I0 = {
  name: '', company: '', dept: 'Meta', phone: '', email: '',
  retie: '', years: '', types: [], maxKwp: '',
  installerType: 'tecnico', copniaNumber: '', conteNumber: '', nit: '',
};

// Documentos requeridos por tipo de instalador (RETIE 2013). A nivel de módulo para
// reusarlo en la validación del submit Y en el render.
//
// 🔑 Decisión del dueño (15-sep): lo que habilita la revisión es la TARJETA PROFESIONAL junto con el
// CERTIFICADO DE VIGENCIA expedido hace no más de 30 días. Quien decide es el servidor
// (`server/certificado-vigencia.js`, también al APROBAR); acá solo se pide el dato.
const DIAS_VIGENCIA = 30;
function requiredDocsFor(installerType) {
  if (installerType === 'ingeniero') {
    return [
      { key: 'tarjeta_profesional', label: 'Tarjeta profesional COPNIA', required: true },
      { key: 'certificado_vigencia', label: `Certificado de vigencia COPNIA (expedido hace ≤ ${DIAS_VIGENCIA} días)`, required: true },
      { key: 'diploma', label: 'Diploma universitario', required: true },
      { key: 'hoja_de_vida', label: 'Hoja de vida', required: true },
    ];
  }
  // 🏢 La empresa NO sube papeles acá (dueño, 3-nov-2026): la habilita ALEBAS (root) por NIT con su
  // expediente de contratista y el mismo correo; lo que subiera aquí no contaría.
  if (installerType === 'empresa') return [];
  return [
    { key: 'tarjeta_profesional', label: 'Tarjeta profesional CONTE', required: true },
    { key: 'certificado_vigencia', label: `Certificado de vigencia CONTE (expedido hace ≤ ${DIAS_VIGENCIA} días)`, required: true },
    { key: 'hoja_de_vida', label: 'Hoja de vida', required: true },
  ];
}

/** Días desde la expedición, en fecha local. `null` si la fecha no sirve. */
function diasDesde(fechaIso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(fechaIso || ''))) return null;
  const [y, m, d] = fechaIso.split('-').map(Number);
  const hoy = new Date();
  return Math.round((Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()) - Date.UTC(y, m - 1, d)) / 86400000);
}

export default function InstallerReg({ addInstaller }) {
  // Identidad de la suite: el registro va VINCULADO a la cuenta logueada (la app ya está
  // detrás del login). El rol técnico es COMPARTIDO con FluxAI: si ya estás habilitado como
  // técnico en la suite, no necesitas registrarte de nuevo.
  const claims = currentClaims();
  const yaTecnico = isTecnico(claims);
  const claimName = (typeof claims?.name === 'string' && !claims.name.includes('@')) ? claims.name : '';
  const [form, setForm] = useState(() => ({
    ...I0,
    name: claimName,
    email: (typeof claims?.email === 'string' ? claims.email : '') || '',
    company: (typeof claims?.empresa === 'string' ? claims.empresa : '') || '',
    installerType: ['tecnico', 'ingeniero', 'empresa'].includes(claims?.installer_type)
      ? claims.installer_type : 'tecnico',
  }));
  const [done, setDone] = useState(false);
  const [docs, setDocs] = useState([]);
  const [fechaVigencia, setFechaVigencia] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [errorEnvio, setErrorEnvio] = useState(null);
  // El alta RETIE es INTERNA (dueño, 6-sep): el servidor solo la recibe con sesión FIRMADA de la
  // suite (la del mismo correo) o de un admin. Una sesión local del navegador (`alg:none`) no
  // alcanza, así que ni se muestra el formulario: llenarlo para recibir un 401 al final es peor.
  const tokenEnvio = tokenParaBackend() || '';
  const sesionFirmada = isSignedToken(tokenEnvio);

  const u = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFormErrors(e => ({ ...e, [k]: undefined })); };
  const toggleType = t => setForm(p => ({
    ...p, types: p.types.includes(t) ? p.types.filter(x => x !== t) : [...p.types, t]
  }));

  const submit = async () => {
    const { valid, errors } = validateContactForm({ name: form.name, email: form.email, phone: form.phone });
    if (!valid) { setFormErrors(errors); return; }
    if (!form.retie) { setFormErrors(e => ({ ...e, retie: 'Certificación RETIE requerida' })); return; }

    // Verificación de credenciales y documentos del técnico/ingeniero (validación nativa).
    // Es el rol que MONETIZA (instala + da soporte) → no se acepta sin documentación completa.
    const isIng = form.installerType === 'ingeniero';
    const isEmp = form.installerType === 'empresa';
    const credErrs = {};
    if (isEmp) {
      if (!form.company?.trim()) credErrs.company = 'La empresa instaladora necesita razón social.';
      if (!form.nit?.trim()) credErrs.nit = 'NIT de la empresa requerido.';
    } else if (isIng) {
      if (!form.copniaNumber?.trim()) credErrs.copniaNumber = 'Matrícula profesional COPNIA requerida.';
      else if (!/^[0-9][0-9.\-\s]{4,}$/.test(form.copniaNumber.trim())) credErrs.copniaNumber = 'Matrícula COPNIA inválida (solo números y guiones).';
    } else {
      if (!form.conteNumber?.trim()) credErrs.conteNumber = 'N° de certificado CONTE requerido.';
      else if (!/^[A-Za-z0-9][A-Za-z0-9\-/\s]{4,}$/.test(form.conteNumber.trim())) credErrs.conteNumber = 'N° CONTE inválido (ej. CONTE-2024-XXXXXX).';
    }
    const faltan = requiredDocsFor(form.installerType).filter(d => d.required && !docs.some(x => x.doc_type === d.key));
    const dias = diasDesde(fechaVigencia);
    const errVigencia = (faltan.length || isEmp) ? null
      : dias == null ? 'Indica la fecha de expedición del certificado de vigencia.'
        : dias < 0 ? 'La fecha de expedición del certificado no puede estar en el futuro.'
          : dias > DIAS_VIGENCIA ? `El certificado de vigencia tiene ${dias} días: pide uno expedido hace ${DIAS_VIGENCIA} días o menos.`
            : null;
    if (Object.keys(credErrs).length || faltan.length || errVigencia) {
      setFormErrors(e => ({
        ...e, ...credErrs,
        docs: faltan.length ? `Faltan documentos obligatorios: ${faltan.map(d => d.label).join(', ')}.` : (errVigencia || undefined),
      }));
      return;
    }

    setSubmitting(true);
    setErrorEnvio(null);
    try {
      // 🔴 Antes el envío era «best-effort»: si el servidor fallaba se tragaba el error y la
      // pantalla decía «¡Solicitud recibida!» con los documentos sin guardar en ningún lado. Ahora
      // se guarda PRIMERO en el servidor, y solo con un `ok` se da por recibida.
      const r = await hubPost('installer-credentials', {
        method: 'SUBMIT',
        technician_id: null, // backend matches by email
        name: form.name,
        email: form.email,
        phone: form.phone,
        installer_type: form.installerType || 'tecnico',
        copnia_number: form.copniaNumber || null,
        conte_number: form.conteNumber || null,
        // El NIT de la empresa: con él (y este correo) root dice si está habilitada.
        nit: isEmp ? form.nit : undefined,
        // La fecha viaja PEGADA al certificado: el servidor la guarda en `fecha_expedicion` y la
        // vuelve a contar el día que el admin aprueba.
        documents: isEmp ? [] : docs.map((d) => (d.doc_type === 'certificado_vigencia' ? { ...d, fecha_expedicion: fechaVigencia } : d)),
        token: tokenEnvio,
      }).catch((e) => ({ ok: false, error: e?.message }));
      if (!r || r.ok !== true) {
        setErrorEnvio(r?.error || 'No se pudo guardar el registro: no quedó recibido. Intenta de nuevo.');
        return;
      }

      const installer = {
        ...form, id: Date.now(),
        // Vincula el registro a la cuenta logueada (base compartida de la suite):
        // al aprobar la verificación, este correo/cuenta obtiene el rol técnico en FluxAI y Solar Hub.
        owner: claims?.email || null,
        accountRole: claims?.role || null,
        date: new Date().toLocaleDateString('es-CO'),
        status: 'pendiente',
      };
      addInstaller(installer);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const ss = {
    wrap: { maxWidth: 680, margin: '0 auto', padding: '20px 14px' },
    card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 22px', marginBottom: 12 },
    lbl: { display: 'block', fontSize: 10, color: C.muted, marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
    inp: { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 11px', color: C.text, fontSize: 13, boxSizing: 'border-box', fontFamily: 'Outfit' },
    btn: { padding: '10px 24px', background: C.teal, color: C.heading, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'Outfit' },
  };

  if (done) return (
    <div style={ss.wrap}>
      <div style={{ ...ss.card, textAlign: 'center', padding: '52px 22px' }}>
 <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${C.teal}22`, border: `2px solid ${C.teal}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 14px', color: C.teal }}>✓</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.heading, marginBottom: 8 }}>¡Solicitud recibida!</div>
        <div style={{ color: C.muted, fontSize: 13, maxWidth: 380, margin: '0 auto 10px', lineHeight: 1.7 }}>
          Gracias <strong style={{ color: C.heading }}>{form.name}</strong>. Revisaremos tu información y tus documentos en 2–3 días hábiles.
        </div>
        <div style={{ fontSize: 12, color: C.amber, marginBottom: 20 }}>
          Recibirás una notificación cuando tus credenciales sean verificadas.
        </div>
        <button style={ss.btn} onClick={() => { setDone(false); setForm(I0); setDocs([]); setFechaVigencia(''); }}>Nuevo registro</button>
      </div>
    </div>
  );

  if (!sesionFirmada) return (
    <div style={ss.wrap}>
      <div style={{ ...ss.card, textAlign: 'center', padding: '40px 22px' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: C.heading, marginBottom: 8 }}>Registro de instaladores RETIE</div>
        <div style={{ color: C.muted, fontSize: 13, maxWidth: 420, margin: '0 auto', lineHeight: 1.7 }}>
          Para inscribirte a la red de instaladores de ALEBAS <strong style={{ color: C.heading }}>entra con tu cuenta de Google</strong>:
          así verificamos tu correo. Después subes tu tarjeta profesional y el certificado de vigencia
          (expedido hace 30 días o menos), y un administrador de ALEBAS revisa tus documentos.
        </div>
      </div>
    </div>
  );

  const requiredDocs = requiredDocsFor(form.installerType);

  return (
    <div style={ss.wrap}>
      {/* RETIE verification warning */}
      <div style={{
        background: `${C.amber}12`, border: `1px solid ${C.amber}30`, borderRadius: 10,
        padding: '12px 16px', marginBottom: 14, fontSize: 13, color: C.amber,
      }}>
         <strong>Verificación RETIE requerida:</strong> Para activar tu cuenta necesitamos verificar tus credenciales.
        La revisión toma 2–3 días hábiles.
      </div>

      {/* Identidad de la suite: el registro va vinculado a la cuenta logueada; el rol técnico
          se comparte con FluxAI (una sola base). Si ya eres técnico habilitado, se reconoce. */}
      {yaTecnico ? (
        <div style={{ background: `${C.green}14`, border: `1px solid ${C.green}44`, borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: C.green }}>
 ✓ <strong>Ya eres técnico de la suite Alebas.</strong> Tu rol está activo en FluxAI y Solar Hub
          ({profileLabel(claims)}). Recibes proyectos por matching y monetizas instalando y dando soporte.
          Puedes actualizar tus credenciales RETIE/documentos abajo si lo necesitas.
        </div>
      ) : (
        <div style={{ background: `${C.teal}10`, border: `1px solid ${C.teal}33`, borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
           Registro vinculado a tu cuenta <strong style={{ color: C.heading }}>{claims?.email || profileLabel(claims)}</strong>.
          Al aprobar la verificación de documentos, tu cuenta obtiene el <strong style={{ color: C.heading }}>rol técnico</strong>,
          que es <strong style={{ color: C.heading }}>compartido entre FluxAI y Solar Hub</strong> (una sola base de identidad).
        </div>
      )}

      <div style={{ ...ss.card, borderColor: C.teal }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 32 }}></span>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.heading }}>Red de instaladores ALEBAS</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Conectamos proyectos con técnicos certificados en todo Colombia</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 9, marginBottom: 12 }}>
        {[['', 'Cobertura nacional', 'Proyectos en todas las regiones'], ['', 'Ingresos adicionales', 'Comisiones por proyecto cerrado'], ['', 'Proyectos calificados', 'Solo solicitudes de tu región']].map(([ic, t, d]) => (
          <div key={t} style={{ ...ss.card, flex: 1, textAlign: 'center', padding: '14px 10px' }}>
            <div style={{ fontSize: 20, marginBottom: 5 }}>{ic}</div>
            <div style={{ fontWeight: 600, color: C.teal, fontSize: 11, marginBottom: 3 }}>{t}</div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{d}</div>
          </div>
        ))}
      </div>

      <div style={ss.card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.heading, marginBottom: 16 }}>Formulario de registro</div>

        {/* Installer type selector — top of form */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>Tipo de instalador *</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { val: 'tecnico', label: 'Técnico', sub: 'Persona · CONTE' },
              { val: 'ingeniero', label: 'Ingeniero', sub: 'Persona · COPNIA' },
              { val: 'empresa', label: 'Empresa', sub: 'También instala' },
            ].map(opt => (
              <button key={opt.val} type="button"
                onClick={() => setForm(f => ({ ...f, installerType: opt.val }))}
                style={{
                  flex: 1, padding: '12px 8px', borderRadius: 10, cursor: 'pointer',
                  border: `2px solid ${form.installerType === opt.val ? C.teal : C.border}`,
                  background: form.installerType === opt.val ? `${C.teal}18` : C.dark,
                  color: form.installerType === opt.val ? C.text : C.muted,
                  fontFamily: 'Outfit', transition: 'all 0.2s',
                }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</div>
                <div style={{ fontSize: 11, marginTop: 3, color: C.muted }}>{opt.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Name + company */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 11 }}>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Nombre completo *</label>
            <input style={{ ...ss.inp, borderColor: formErrors.name ? C.red : undefined }} value={form.name} onChange={e => u('name', e.target.value)} placeholder="Tu nombre" />
            {formErrors.name && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.name}</span>}
          </div>
          <div style={{ flex: 1 }}><label style={ss.lbl}>Empresa (si aplica)</label><input style={ss.inp} value={form.company} onChange={e => u('company', e.target.value)} placeholder="Empresa o independiente" /></div>
        </div>

        {/* Phone + email */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 11 }}>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Teléfono / WhatsApp *</label>
            <input style={{ ...ss.inp, borderColor: formErrors.phone ? C.red : undefined }} value={form.phone} onChange={e => u('phone', e.target.value)} placeholder="300 000 0000" />
            {formErrors.phone && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.phone}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Email *</label>
            <input style={{ ...ss.inp, borderColor: formErrors.email ? C.red : undefined }} value={form.email} onChange={e => u('email', e.target.value)} placeholder="tu@email.com" />
            {formErrors.email && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.email}</span>}
          </div>
        </div>

        {/* Dept + RETIE */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 11 }}>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Departamento principal *</label>
            <select style={{ ...ss.inp, cursor: 'pointer' }} value={form.dept} onChange={e => u('dept', e.target.value)}>
              {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Certificación RETIE *</label>
            <input style={{ ...ss.inp, borderColor: formErrors.retie ? C.red : undefined }} value={form.retie} onChange={e => u('retie', e.target.value)} placeholder="Número de certificado" />
            {formErrors.retie && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.retie}</span>}
          </div>
        </div>

        {/* Conditional credential number field */}
        {form.installerType === 'empresa' ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, color: C.muted }}>NIT de la empresa *</label>
            <input value={form.nit || ''} onChange={e => setForm(f => ({ ...f, nit: e.target.value }))}
              placeholder="901.XXX.XXX (sin dígito de verificación, o con guion)"
              style={{ width: '100%', background: C.dark, border: `1px solid ${formErrors.nit ? C.red : C.border}`, borderRadius: 10,
                color: C.text, padding: '10px 12px', fontSize: 14, fontFamily: 'Outfit', boxSizing: 'border-box', marginTop: 4 }} />
            {formErrors.nit && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.nit}</span>}
            {formErrors.company && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.company}</span>}
          </div>
        ) : form.installerType === 'ingeniero' ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, color: C.muted }}>N° Tarjeta Profesional COPNIA *</label>
            <input value={form.copniaNumber || ''} onChange={e => setForm(f => ({ ...f, copniaNumber: e.target.value }))}
              placeholder="ej: 12345678901CP"
              style={{ width: '100%', background: C.dark, border: `1px solid ${formErrors.copniaNumber ? C.red : C.border}`, borderRadius: 10,
                color: C.text, padding: '10px 12px', fontSize: 14, fontFamily: 'Outfit', boxSizing: 'border-box', marginTop: 4 }} />
            {formErrors.copniaNumber && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.copniaNumber}</span>}
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, color: C.muted }}>N° Certificado CONTE *</label>
            <input value={form.conteNumber || ''} onChange={e => setForm(f => ({ ...f, conteNumber: e.target.value }))}
              placeholder="ej: CONTE-2024-XXXXXX"
              style={{ width: '100%', background: C.dark, border: `1px solid ${formErrors.conteNumber ? C.red : C.border}`, borderRadius: 10,
                color: C.text, padding: '10px 12px', fontSize: 14, fontFamily: 'Outfit', boxSizing: 'border-box', marginTop: 4 }} />
            {formErrors.conteNumber && <span style={{ color: C.red, fontSize: 12, marginTop: 3, display: 'block' }}>{formErrors.conteNumber}</span>}
          </div>
        )}

        {/* Experience + capacity */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Años de experiencia</label>
            <select style={{ ...ss.inp, cursor: 'pointer' }} value={form.years} onChange={e => u('years', e.target.value)}>
              {['', '1-2', '3-5', '6-10', 'Más de 10'].map(o => <option key={o} value={o}>{o || 'Seleccionar'}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={ss.lbl}>Capacidad máxima (kWp/mes)</label>
            <input type="number" style={ss.inp} value={form.maxKwp} onChange={e => u('maxKwp', e.target.value)} placeholder="Ej: 50" />
          </div>
        </div>

        {/* Project types */}
        <div style={{ marginBottom: 14 }}>
          <label style={ss.lbl}>Tipos de proyectos</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['Residencial', 'Comercial', 'Industrial', 'Agroindustrial', 'Rural / Off-grid'].map(t => (
              <div key={t} onClick={() => toggleType(t)} style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 11, border: `1.5px solid ${form.types.includes(t) ? C.teal : C.border}`, background: form.types.includes(t) ? `${C.teal}20` : 'transparent', color: form.types.includes(t) ? C.teal : C.muted, fontWeight: form.types.includes(t) ? 600 : 400 }}>{t}</div>
            ))}
          </div>
        </div>

        {form.installerType === 'empresa' && (
          <div style={{ marginBottom: 20, background: C.card, borderRadius: 14, padding: 18, border: `1px solid ${C.border}`, fontSize: 12.5, color: C.text, lineHeight: 1.5 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Tu empresa la habilita ALEBAS por NIT</div>
            Para que tu empresa pueda operar, tiene que estar inscrita en ALEBAS como contratista (persona jurídica con NIT),
            con estos papeles cargados allá: cámara de comercio expedida hace 30 días o menos, RUT y cédula del representante legal.
            El correo inscrito allá debe ser <strong>el mismo de esta cuenta</strong>. Aquí no se suben documentos de la empresa:
            los de Solar Hub no cuentan para la habilitación. Para inscribirla escribe a{' '}
            <a href="mailto:info@solar-hub.co" style={{ color: C.teal }}>info@solar-hub.co</a>.
          </div>
        )}

        {/* Document upload section */}
        {requiredDocs.length > 0 && <div style={{ marginBottom: 20, background: C.card, borderRadius: 14, padding: 18, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
             Documentos requeridos (RETIE 2013)
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
            {requiredDocs.map((d) => d.label).join(' · ')}
          </div>

          {requiredDocs.map(docDef => {
            const uploaded = docs.find(d => d.doc_type === docDef.key);
            return (
              <div key={docDef.key} style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                background: C.dark, borderRadius: 8, padding: '10px 12px',
              }}>
                <span style={{ fontSize: 18 }}>{uploaded ? '' : ''}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: uploaded ? C.green : C.text }}>{docDef.label}</div>
                  {uploaded && <div style={{ fontSize: 11, color: C.muted }}>{uploaded.file_name}</div>}
                  {docDef.key === 'certificado_vigencia' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: C.muted }}>
                      Fecha de expedición
                      <input type="date" value={fechaVigencia} onChange={(e) => setFechaVigencia(e.target.value)}
                        style={{ ...ss.inp, width: 'auto', padding: '4px 8px', fontSize: 12 }} />
                      {(() => {
                        const n = diasDesde(fechaVigencia);
                        if (n == null) return null;
                        const bien = n >= 0 && n <= DIAS_VIGENCIA;
                        return <span style={{ color: bien ? C.green : C.red }}>{n < 0 ? 'fecha futura' : `hace ${n} días`}</span>;
                      })()}
                    </label>
                  )}
                </div>
                <label style={{
                  cursor: 'pointer', background: C.teal, color: C.heading, borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, fontFamily: 'Outfit', fontWeight: 600,
                }}>
                  {uploaded ? 'Cambiar' : 'Subir'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { alert('Archivo máximo 5 MB'); return; }
                      const reader = new FileReader();
                      reader.onload = ev => {
                        const b64 = ev.target.result.split(',')[1];
                        setDocs(prev => [
                          ...prev.filter(d => d.doc_type !== docDef.key),
                          { doc_type: docDef.key, file_name: file.name, file_size_kb: Math.round(file.size / 1024), file_data_b64: b64 },
                        ]);
                      };
                      reader.readAsDataURL(file);
                    }} />
                </label>
              </div>
            );
          })}

          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
            Formatos: PDF, JPG, PNG · Máximo 5 MB por archivo · Tu información está protegida (Ley 1581/2012)
          </div>
          {formErrors.docs && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.red, background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: '8px 10px' }}> {formErrors.docs}</div>
          )}
        </div>}

        <div style={{ background: `${C.teal}10`, borderRadius: 6, padding: '9px 12px', marginBottom: 14, fontSize: 10, color: C.muted }}>
          Al registrarte aceptas los términos de la red ALEBAS. Tu información será validada antes de asignarte proyectos.
        </div>
        {errorEnvio && (
          <div role="alert" style={{ marginBottom: 10, fontSize: 12, color: C.red, background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: '8px 10px' }}>
            {errorEnvio}
          </div>
        )}
        <button
          style={{ ...ss.btn, width: '100%', padding: '12px', opacity: submitting ? 0.4 : 1 }}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Enviando...' : 'Enviar solicitud de vinculación →'}
        </button>
      </div>
    </div>
  );
}
